import { companionKeys, COMPANION_PATH } from '@/hooks/queries/companion-keys';
import api from '@/libs/axiosInstance';
import type { ApiResponse, CompanionFeedDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

const ONE_MINUTE_MS = 60 * 1000;

/**
 * The brand's daily briefing, read and nothing else.
 *
 * Reading is never a gesture: this hook marks nothing seen and records no
 * opening. Those are mutations the panel sends when a person actually looks at
 * a card, so a background refetch can never inflate what the metrics count.
 */
const fetchCompanionFeed = async (): Promise<CompanionFeedDto> => {
  const { data } = await api.get<ApiResponse<CompanionFeedDto>>(`${COMPANION_PATH}/feed`);
  if (!data.data) throw new Error('Empty companion feed response');
  return data.data;
};

export const useCompanionFeedQuery = (brandId: string) =>
  useQuery<CompanionFeedDto, Error>({
    queryKey: companionKeys.brand(brandId),
    queryFn: fetchCompanionFeed,
    enabled: !!brandId,
    // A card can be published at any moment by the delivery job, and the panel
    // is a live surface: a short staleness plus focus and reconnect refetches
    // keeps it honest without polling.
    staleTime: ONE_MINUTE_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
